<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" @click.self="close">
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200">
      <!-- 标题 -->
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-xl font-semibold">分享我的RPI评估结果</h3>
        <button @click="close" class="text-gray-400 hover:text-gray-600 transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5">
            <path d="M18 6 6 18"></path>
            <path d="m6 6 12 12"></path>
          </svg>
        </button>
      </div>

      <p class="text-sm text-gray-600 mb-6">
        选择您喜欢的方式分享评估结果，所有数据都经过匿名化处理
      </p>

      <!-- 分享选项 -->
      <div class="space-y-3">
        <!-- 复制链接 -->
        <button 
          @click="copyLink"
          class="w-full flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-psychology-primary hover:bg-psychology-primary/5 transition-all"
        >
          <div class="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5 text-gray-600">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
            </svg>
          </div>
          <div class="flex-1 text-left">
            <div class="font-medium">复制链接</div>
            <div class="text-xs text-gray-500">复制到剪贴板后分享</div>
          </div>
        </button>

        <!-- 微信分享 -->
        <button 
          @click="shareToWechat"
          class="w-full flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all"
        >
          <div class="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
            <span class="text-lg">💬</span>
          </div>
          <div class="flex-1 text-left">
            <div class="font-medium">微信</div>
            <div class="text-xs text-gray-500">分享到微信好友或朋友圈</div>
          </div>
        </button>

        <!-- 微博分享 -->
        <button 
          @click="shareToWeibo"
          class="w-full flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-red-500 hover:bg-red-50 transition-all"
        >
          <div class="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
            <span class="text-lg">📱</span>
          </div>
          <div class="flex-1 text-left">
            <div class="font-medium">微博</div>
            <div class="text-xs text-gray-500">分享到新浪微博</div>
          </div>
        </button>

        <!-- QQ分享 -->
        <button 
          @click="shareToQQ"
          class="w-full flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all"
        >
          <div class="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <span class="text-lg">🐧</span>
          </div>
          <div class="flex-1 text-left">
            <div class="font-medium">QQ</div>
            <div class="text-xs text-gray-500">分享到QQ好友或空间</div>
          </div>
        </button>
      </div>

      <!-- 二维码 -->
      <div v-if="showQRCode" class="mt-6 p-4 bg-gray-50 rounded-lg text-center">
        <div class="text-sm text-gray-600 mb-3">扫描二维码查看结果</div>
        <div class="inline-block p-4 bg-white rounded-lg">
          <div ref="qrcodeContainer" class="w-48 h-48"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QRCode from 'qrcode'
import { useToast } from 'vue-toastification'

export default {
  name: 'ShareDialog',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    rpiScore: {
      type: Number,
      required: true
    },
    rpiLevel: {
      type: Object,
      required: true
    }
  },
  setup() {
    const toast = useToast()
    return { toast }
  },
  data() {
    return {
      showQRCode: false
    }
  },
  methods: {
    close() {
      this.$emit('close')
      this.showQRCode = false
    },
    async copyLink() {
      const url = window.location.href
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(url)
          this.toast.success('链接已复制到剪贴板！')
        } else {
          // 降级方案
          const textArea = document.createElement('textarea')
          textArea.value = url
          textArea.style.position = 'fixed'
          textArea.style.left = '-999999px'
          document.body.appendChild(textArea)
          textArea.focus()
          textArea.select()
          try {
            document.execCommand('copy')
            this.toast.success('链接已复制到剪贴板！')
          } catch (err) {
            this.toast.error('复制失败，请手动复制')
          }
          document.body.removeChild(textArea)
        }
      } catch (err) {
        this.toast.error('复制失败，请手动复制')
      }
    },
    shareToWechat() {
      // 微信分享需要在移动端打开或使用二维码
      this.toast.info('请使用微信扫一扫功能分享', {
        timeout: 3000
      })
      this.showQRCode = true
      this.$nextTick(() => {
        this.generateQRCode()
      })
    },
    shareToWeibo() {
      const url = encodeURIComponent(window.location.href)
      const title = encodeURIComponent(`我的恋爱占有欲指数是${this.rpiScore}，${this.rpiLevel.level}！快来测测你的吧~`)
      const shareUrl = `https://service.weibo.com/share/share.php?url=${url}&title=${title}`
      window.open(shareUrl, '_blank', 'width=600,height=400')
    },
    shareToQQ() {
      const url = encodeURIComponent(window.location.href)
      const title = encodeURIComponent(`我的恋爱占有欲指数是${this.rpiScore}`)
      const desc = encodeURIComponent(`${this.rpiLevel.level}，快来测测你的吧~`)
      const shareUrl = `https://connect.qq.com/widget/shareqq/index.html?url=${url}&title=${title}&desc=${desc}`
      window.open(shareUrl, '_blank', 'width=600,height=400')
    },
    async generateQRCode() {
      // 使用 qrcode 库生成二维码
      if (this.$refs.qrcodeContainer) {
        try {
          const url = window.location.href
          this.$refs.qrcodeContainer.innerHTML = '' // 清空容器
          
          // 生成二维码
          await QRCode.toCanvas(url, {
            width: 192,
            margin: 2,
            color: {
              dark: '#000000',
              light: '#FFFFFF'
            }
          }).then(canvas => {
            this.$refs.qrcodeContainer.appendChild(canvas)
          })
        } catch (err) {
          console.error('二维码生成失败:', err)
          this.$refs.qrcodeContainer.innerHTML = '<div class="text-red-500 text-sm">二维码生成失败</div>'
          this.toast.error('二维码生成失败，请使用复制链接功能')
        }
      }
    }
  }
}
</script>

